<template>
  <div class="container">
    <v-progress-linear
      v-if="isLoading"
      indeterminate
    />
    <!-- Keep the div(AR type)  sorted by alphabet -->
    <div v-else>
      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.CONTENANCE)"
        :title="$t('analysisRequests.contenanceAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'ContenanceSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.CONTENANCE"
          :analysisRequests="contenanceAnalysisRequests"
          :sampleAttributes="analysisRequestFields.CONTENANCE"
          :analysisTypeList="analysisTypeList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :analysis="contenanceAnalysis"
          @reloadAnalysis="getDatas"
        />
      </CollapsibleSection>


      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.COUPON)"
        :title="$t('analysisRequests.couponAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'CouponSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.COUPON"
          :analysisRequests="couponAnalysisRequests"
          :sampleAttributes="analysisRequestFields.COUPON"
          :analysisTypeList="analysisTypeList"
          :standardFamilyList="standardFamilyList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :analysis="couponAnalysis"
          @reloadAnalysis="getDatas"
        />
        <CouponCaption
          :analysisRequests="couponAnalysisRequests"
        />
      </CollapsibleSection>

      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.DEPOSIT)"
        :title="$t('analysisRequests.depositAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'DepositSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.DEPOSIT"
          :analysisRequests="depositAnalysisRequests"
          :sampleAttributes="analysisRequestFields.DEPOSIT"
          :depositCompositionList="depositCompositionList"
          :analysisTypeList="analysisTypeList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :analysis="depositAnalysis"
          @reloadAnalysis="getDatas"
        />
      </CollapsibleSection>

      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.GLYCOL)"
        :title="$t('analysisRequests.glycolAnalysis')"
        :openedChildren="elementWillOpenedFirst=== 'GlycolSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.GLYCOL"
          :analysisRequests="glycolAnalysisRequests"
          :sampleAttributes="analysisRequestFields.GLYCOL"
          :analysisTypeList="analysisTypeList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :glycolTypeList="glycolTypeList"
          :standardFamilyList="standardFamilyList"
          :analysis="glycolAnalysis"
          @reloadAnalysis="getDatas"
        />
      </CollapsibleSection>

      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.LEGIONELLA_CULTRURE)"
        :title="$t('analysisRequests.legionellaCultureAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'LegionellaCultureSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.LEGIONELLA_CULTRURE"
          :analysisRequests="legionelAnalysisRequests"
          :sampleAttributes="analysisRequestFields.LEGIONELL_CULTURE"
          :analysisTypeList="legionellaAnalysisTypeList"
          :standardFamilyList="standardFamilyList"
          :clients="clients"
          :sites="sites"
          :skipFields="['resultInterpretation', 'analyses']"
          :isSummaryView="isSummaryView"
          :analysis="legionelCoulturAnalysis"
          @reloadAnalysis="getDatas"
        />
        <v-row
          v-if="displayedLegioStandards.length"
          noGutters
          style="margin-top: 20px"
        >
          <v-col
            cols="auto"
            style="width: 174px;"
          >
            <div class="resultInterpretation--container">
              <span>{{ $t('labWorkOrderTable.labels.resultInterpretation') }}</span>
            </div>
          </v-col>
          <v-col
            cols="10"
            class="d-flex flex-row"
          >
            <div
              v-for="standard in displayedLegioStandards"
              :key="standard.id"
              class="legionnelStandard--container"
            >
              <LegionellaCaption
                v-if="standard.norm"
                :legionnelStandard="standard"
              />
            </div>
          </v-col>
        </v-row>
      </CollapsibleSection>


      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.LEGIONELLOSIS)"
        :title="$t('analysisRequests.legionellosisAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'LegionellosisSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.LEGIONELLOSIS"
          :analysisRequests="legionelAnalysisRequests"
          :sampleAttributes="analysisRequestFields.LEGIONELLOSIS"
          :analysisTypeList="legionellaAnalysisTypeList"
          :standardFamilyList="standardFamilyList"
          :clients="clients"
          :sites="sites"
          :skipFields="['resultInterpretation', 'analyses']"
          :isSummaryView="isSummaryView"
          :analysis="legionelAnalysis"
          @reloadAnalysis="getDatas"
        />
        <v-row
          v-if="displayedLegioStandards.length"
          noGutters
          style="margin-top: 20px"
        >
          <v-col
            cols="auto"
            style="width: 174px;"
          >
            <div class="resultInterpretation--container">
              <span>{{ $t('labWorkOrderTable.labels.resultInterpretation') }}</span>
            </div>
          </v-col>
          <v-col
            cols="10"
            class="d-flex flex-row"
          >
            <div
              v-for="standard in displayedLegioStandards"
              :key="standard.id"
              class="legionnelStandard--container"
            >
              <LegionellaCaption
                v-if="standard.norm"
                :legionnelStandard="standard"
              />
            </div>
          </v-col>
        </v-row>
      </CollapsibleSection>

      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.OTHER)"
        :title="$t('analysisRequests.otherAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'OtherSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.OTHER"
          :analysisRequests="otherAnalysisRequests"
          :sampleAttributes="analysisRequestFields.OTHER"
          :analysisTypeList="analysisTypeList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :standardFamilyList="standardFamilyList"
          :analysis="otherAnalysis"
          @reloadAnalysis="getDatas"
        />
      </CollapsibleSection>

      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.RESIN)"
        :title="$t('analysisRequests.resinAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'ResinSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.RESIN"
          :analysisRequests="resinAnalysisRequests"
          :sampleAttributes="analysisRequestFields.RESIN"
          :analysisTypeList="analysisTypeList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :standardFamilyList="standardFamilyList"
          :analysis="resinAnalysis"
          @reloadAnalysis="getDatas"
        />
      </CollapsibleSection>

      <CollapsibleSection
        v-if="isAnalysisRequestType(sampleTypes.WATER)"
        :title="$t('analysisRequests.waterAnalysis')"
        :openedChildren="elementWillOpenedFirst === 'WaterSample' "
      >
        <AnalysisRequestTable
          :scope="sampleTypes.WATER"
          :analysisRequests="waterAnalysisRequests"
          :sampleAttributes="analysisRequestFields.WATER"
          :analysisTypeList="analysisTypeList"
          :clients="clients"
          :sites="sites"
          :isSummaryView="isSummaryView"
          :standardFamilyList="standardFamilyList"
          :analysis="waterAnalysis"
          @reloadAnalysis="getDatas"
        />
      </CollapsibleSection>
    </div>
  </div>
</template>

<script>
import { isEmpty, cloneDeep } from 'lodash';
import CollapsibleSection from '@/components/shared/containers/CollapsibleSection';
import AnalysisRequestTable from '@/views/laboratory/workOrder/AnalysisRequestTable';
import CouponCaption from '@/views/laboratory/workOrder/summary/CouponCaption';
import analysisRequestFields from '@/constants/analysisRequestFields';
import sampleTypes from '@/constants/sampleTypes';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import { mapGetters, mapActions } from 'vuex';
import LegionellaCaption from '@/views/laboratory/workOrder/summary/standardsInterpretation/LegionellaCaption';

export default {
    components: { LegionellaCaption, CollapsibleSection, AnalysisRequestTable, CouponCaption },
    props: {
        isSummaryView: {
            type: Boolean,
            default: false,
        },
        analysisRequests: {
            type: Array,
            default: () => ([]),
        },
    },
    data() {
        return {
            analysisRequestFields,
            sampleTypes,
            analysisTypeList: [],
            clients: [],
            sites: [],
            analysis: [],
            depositCompositionList: [],
            standardFamilyList: [],
            legionellaAnalysisTypeList: [],
            glycolTypeList: [],
            isLoading: true,
            empty_option: {
                id: null,
                name: this.$t('labWorkOrderTable.labels.none'),
            },
            elementWillOpenedFirst: '',
            mutableAnalysisRequests: [],
        };
    },
    computed: {
        ...mapGetters([
            'labWorkOrder',
        ]),
        waterAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.WATER);
        },
        glycolAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.GLYCOL);
        },
        resinAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.RESIN);
        },
        legionelAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.LEGIONELLOSIS || item.type === sampleTypes.LEGIONELLA_CULTRURE);
        },
        contenanceAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.CONTENANCE);
        },
        depositAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.DEPOSIT);
        },
        couponAnalysisRequests() {
            let couponAnalysisRequests = this.mutableAnalysisRequests.filter(item => item.type === sampleTypes.COUPON);
            const digitsAfterDecimal = 4;
            couponAnalysisRequests = couponAnalysisRequests.map(analysisRequest => ({
                ...analysisRequest,
                samples: analysisRequest.samples.map(sample => {
                    if (sample) {
                        const initialValue = sample.initialWeight ? sample.initialWeight / 1000 : null;
                        const finalValue = sample.finalWeight ? sample.finalWeight / 1000 : null;

                        const isInitialWeightMoreThan4Digits = initialValue ? (initialValue).toString().split('.')[1]?.length > digitsAfterDecimal : false;
                        const isFinalWeightMoreThan4Digits = finalValue ? (finalValue).toString().split('.')[1]?.length > digitsAfterDecimal : false;

                        // toFixed only if we have more than digitsAfterDecimal digits to avoid having 0 placeholder. e.g: 11.9 -> 11.9000
                        const finalWeight = isFinalWeightMoreThan4Digits ? (finalValue).toFixed(digitsAfterDecimal) : finalValue;
                        const initialWeight = isInitialWeightMoreThan4Digits ? (initialValue).toFixed(digitsAfterDecimal) : initialValue;
                        return {
                            ...sample,
                            columnId: `${analysisRequest.requestNumber}-${sample.identification}`,
                            initialWeight,
                            finalWeight,
                        };
                    }
                    return sample;
                }),
            }));
            return couponAnalysisRequests;
        },
        otherAnalysisRequests() {
            return this.analysisRequests.filter(item => item.type === sampleTypes.OTHER);
        },
        displayedLegioStandards() {
            const [uniqStandards] = this.removeStandardDuplicated(this.mutableAnalysisRequests);
            if (!uniqStandards) {
                return [];
            }
            const applicableNormIds = [];
            this.mutableAnalysisRequests.forEach(item => {
                if (item.applicableNorms) {
                    applicableNormIds.push(item.applicableNorms.id);
                }
            });
            let displayedStandards = [];
            if (!applicableNormIds?.length) {
                return displayedStandards;
            }
            const correspondantStandardFamily = this.standardFamilyList.find(family => family.id === uniqStandards?.id);
            displayedStandards = uniqStandards?.analyses?.map(analysis => {
                // eslint-disable-next-line no-underscore-dangle
                const _analysis = cloneDeep(analysis);
                correspondantStandardFamily.standards.forEach(standard => {
                    if (standard.analysisType?.id && standard.analysisType?.name && analysis.analysisTypeId === standard.analysisType.id) {
                        _analysis.name = standard.analysisType.name; // we get the name with translation
                    }
                });
                return _analysis;
            });
            return displayedStandards;
        },
        waterAnalysis() {
            return this.analysis[sampleTypes.WATER];
        },
        glycolAnalysis() {
            return this.analysis[sampleTypes.GLYCOL];
        },
        resinAnalysis() {
            return this.analysis[sampleTypes.RESIN];
        },
        legionelAnalysis() {
            return this.analysis[sampleTypes.LEGIONELLOSIS];
        },
        legionelCoulturAnalysis() {
            return this.analysis[sampleTypes.LEGIONELLA_CULTRURE];
        },
        contenanceAnalysis() {
            return this.analysis[sampleTypes.CONTENANCE];
        },
        depositAnalysis() {
            return this.analysis[sampleTypes.DEPOSIT];
        },
        couponAnalysis() {
            return this.analysis[sampleTypes.COUPON];
        },
        otherAnalysis() {
            return this.analysis[sampleTypes.OTHER];
        },
    },
    async mounted() {
        const [
            analysisTypeListResponse,
            legionellaAnalysisTypeListResponse,
            compositionAnalysisTypeResponse,
            clientsResponse,
            sitesResponse,
            standardFamiliesResponse,
            glycoleResultResponse,
        ] = await Promise.all([
            this.$rq.getAnalysisTypes({ pagination: false, typeName: 'All' }),
            this.$rq.getAnalysisTypes({ typeName: analysisTypesConstant.TYPE_LEGIONELLA, pagination: false }),
            this.$rq.getAnalysisTypes({ typeName: analysisTypesConstant.TYPE_COMPOSITION, pagination: false }),
            this.$rq.getClients({ allClients: true }),
            this.$rq.getSites(),
            this.$rq.getStandardFamilies(),
            this.$rq.getGlycolTypes(),
        ]);

        await this.getDatas();
        this.analysisTypeList = analysisTypeListResponse.data.types.filter(t => t.type === analysisTypesConstant.SIMPLE_ANALYSIS_TYPE
        || t.type === analysisTypesConstant.CALCULATED_ANALYSIS_TYPE
        || t.type === analysisTypesConstant.TYPE_TEXT
        || t.type === analysisTypesConstant.TYPE_LIST);

        this.legionellaAnalysisTypeList = legionellaAnalysisTypeListResponse.data.types;
        this.depositCompositionList = compositionAnalysisTypeResponse.data.types;
        this.clients = clientsResponse.data.clients;
        this.sites = sitesResponse.data.sites;
        this.glycolTypeList = glycoleResultResponse.data.types;
        this.standardFamilyList = [this.empty_option, ...standardFamiliesResponse.data.standardFamilies];

        this.isLoading = false;
        this.showFirstElement(); // open only the first element in the list
        this.mutableAnalysisRequests = this.analysisRequests;
    },
    methods: {
        ...mapActions({
            reloadWorkOrderResults: 'getLabWorkOrderResults',
        }),
        isAnalysisRequestType(type) {
            return this.analysisRequests.some(analysisRequest => analysisRequest.type === type);
        },
        showFirstElement() {
            /* List of Type in order, keep it sorted  */
            const types = ['ContenanceSample', 'CouponSample', 'DepositSample', 'GlycolSample', 'LegionellaCultureSample', 'LegionellosisSample', 'OtherSample', 'ResinSample', 'WaterSample'];
            /* List of Type in this page */
            const typesInResults = this.analysisRequests.map(sampleType => (
                sampleType.type
            ));
            /* First element selected in the list  */
            const firstType = types.find(type => typesInResults.includes(type));
            /* Send element selected in data */
            if (firstType) {
                this.elementWillOpenedFirst = firstType;
                return firstType;
            }
            return null;
        },
        async getDatas(updatedAnalysisRequests = []) {
            if (updatedAnalysisRequests?.length) {
                this.mutableAnalysisRequests = updatedAnalysisRequests;
            }
            const { data } = await this.$rq.getLabWoGrid(this.labWorkOrder.id);
            this.analysis = data;
        },
        removeStandardDuplicated(analysisRequests = []) {
            analysisRequests.forEach(aR => {
                // eslint-disable-next-line no-param-reassign
                if (!aR.standardsToDisplay) aR.standardsToDisplay = [];
                const isStandardExist = aR.standardsToDisplay.find(st => {
                    if (aR && st.id && aR.applicableNorms?.id) {
                        return st.id.toString() === aR.applicableNorms?.id?.toString();
                    }
                    return st.id === aR.applicableNorms?.id;
                });
                    // if it does not exist we add it to the list.
                if (!isStandardExist && aR && aR.applicableNorms?.id) {
                    aR.standardsToDisplay.push({
                        id: aR.applicableNorms.id,
                        applicableNorms: aR.applicableNorms,
                        analyses: aR.analyses?.filter(analisis => !isEmpty(analisis.norm)) || [],
                        measures: aR.type === sampleTypes.LEGIONELLOSIS ? aR.measures : undefined, // for Legio
                    });
                } else {
                    // in case the analysisRequest has analyses that are not
                    // in the standardsToDisplay analysis list.
                    const combinedAnalyses = aR.standardsToDisplay.flatMap(st => st.analyses);
                    // eslint-disable-next-line no-param-reassign
                    aR.standardsToDisplay = aR.standardsToDisplay.map(st => {
                        if (st && isStandardExist) {
                            if (st.id === isStandardExist.id) {
                                aR.analyses.forEach(an => {
                                    // Add analysis if it's not in the combined array and have norm.
                                    const ids = combinedAnalyses.map(combAn => {
                                        if (combAn.analysisTypeId && !isEmpty(combAn.norm)) {
                                            return combAn.analysisTypeId.toString();
                                        }
                                        return null;
                                    });
                                    const index = ids.indexOf(an.analysisTypeId.toString());
                                    if (index === -1 && !isEmpty(an.norm)) {
                                        combinedAnalyses.push(an);
                                    }
                                });
                            }
                        }
                        return {
                            ...st,
                            analyses: [...combinedAnalyses],
                        };
                    });
                }
            });
            return analysisRequests[0].standardsToDisplay;
        },
    },
};


</script>
<style lang="scss" scoped>
  .container {
    max-width: 100%;
  }
  .resultInterpretation--container {
    width: 100%;
    span {
      font-weight: 600;
      font-size: 14px;
    }
  }
.legionnelStandard--container {
  height: 100%;
}
</style>
